// src/pages/SummerCampPage/SummerCampPage.js
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faMapMarkerAlt,
    faUsers,
    faClock,
    faCheck,
    faLightbulb,
    faStar,
    faMagic,
    faPalette,
    faMusic,
    faFlask,
    faComments,
    faPhone,
    faGamepad,
    faCookieBite,
    faSun,
    faTree,
    faLeaf,
    faWater,
    faCampground,
    faArrowRight,
    faCircle,
    faCertificate,
    faChild
} from '@fortawesome/free-solid-svg-icons';

import Navbar from '../../components/common/Navbar/Navbar';
import Footer from '../../components/common/Footer/Footer';
import ScrollToTop from '../../components/common/ScrollToTop/ScrollToTop';
import useScrollReveal from '../../hooks/useScrollReveal';
import './SummerCampPage.css';

const SummerCampPage = () => {
    useScrollReveal();
    const [activeTab, setActiveTab] = useState('activities');

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        document.querySelectorAll('.camp-activities, .camp-schedule, .camp-age-groups').forEach(section => {
            section.style.display = 'none';
        });
        if (tab === 'activities') {
            document.querySelector('.camp-activities').style.display = 'block';
        } else if (tab === 'schedule') {
            document.querySelector('.camp-schedule').style.display = 'block';
        } else if (tab === 'age-groups') {
            document.querySelector('.camp-age-groups').style.display = 'block';
        }
    };

    useEffect(() => {
        const initTabs = () => {
            const scheduleSection = document.querySelector('.camp-schedule');
            const ageGroupsSection = document.querySelector('.camp-age-groups');
            const activitiesSection = document.querySelector('.camp-activities');
            if (scheduleSection && ageGroupsSection && activitiesSection) {
                scheduleSection.style.display = 'none';
                ageGroupsSection.style.display = 'none';
                activitiesSection.style.display = 'block';
            }
        };
        setTimeout(initTabs, 100);
    }, []);

    const activities = [
        {
            icon: faMagic,
            title: "Magic & Performance",
            description: "Children enjoy magic shows and develop confidence through performances and creative expression.",
            features: ["Magic wand crafting", "Puppet shows with sock puppets", "Group storytelling"]
        },
        {
            icon: faPalette,
            title: "Arts & Crafts",
            description: "Children explore various artistic mediums through age-appropriate creative projects.",
            features: ["Pottery with pinch pots", "Sponge painting", "Nature collages & paper crafts"]
        },
        {
            icon: faMusic,
            title: "Dance & Movement",
            description: "Children express themselves through dance, movement activities, and musical experiences.",
            features: ["Creative dance sessions", "Musical games", "Rhythm activities"]
        },
        {
            icon: faGamepad,
            title: "Games & Activities",
            description: "Fun-filled games that develop teamwork, coordination, and problem-solving skills.",
            features: ["Treasure hunts", "Musical chairs", "Team challenges"]
        },
        {
            icon: faFlask,
            title: "Science Experiments",
            description: "Hands-on projects that explore scientific concepts through age-appropriate experiments.",
            features: ["Sink-or-float experiments", "Colorful potion mixing", "Simple chemical reactions"]
        },
        {
            icon: faCookieBite,
            title: "Cookery Day",
            description: "Children learn basic food preparation skills through fun, no-cook recipes.",
            features: ["No-bake desserts", "Fruit salad preparation", "Chocolate mug cakes"]
        }
    ];

    const timelineItems = [
        { time: "9:30 AM", activity: "Welcome Circle", description: "Morning gathering with songs, greetings, and introduction to daily activities" },
        { time: "10:00 AM", activity: "First Activity Session", description: "Age-appropriate activities (Magic show, Dance, Pottery, Balloon Art, etc.)" },
        { time: "11:00 AM", activity: "Break Time", description: "Refreshment break and supervised free play" },
        { time: "11:30 AM", activity: "Second Activity Session", description: "Specialized programs (Craft, Pottery, Pot Painting, etc.)" },
        { time: "12:30 PM", activity: "Third Activity Session", description: "Fun learning activities (Games, Science Experiments, Story Telling, etc.)" },
        { time: "1:00 PM", activity: "Dipersal", description: "Parent pickup and goodbyes" }
    ];

    const ageGroups = [
        {
            name: "Little Adventurers",
            age: "Ages 4-7",
            activities: [
                "Make magic wands with sticks, paper, and glitter",
                "Twist balloon animals and blow bubbles with dish soap",
                "Create leaf prints with paint",
                "Make pinch pots during pottery sessions",
                "Decorate paper chef hats",
                "Prepare fruit salad with seasonal fruits",
                "Get messy with sponge painting",
                "Enjoy puppet shows with sock puppets",
                "Make DIY movie tickets with paper and stamps",
                "Create group collages with old magazines"
            ]
        },
        {
            name: "Big Trailblazers",
            age: "Ages 7+",
            activities: [
                "Learn a simple card trick",
                "Mix a \"magic potion\" with water, food coloring, and glitter",
                "Make a nature collage with glue and paper",
                "Craft small clay figures",
                "Design a dessert menu with paper and crayons",
                "Make chocolate mug cakes",
                "Craft paper plate masks with markers and feathers",
                "Act out a group story using their imagination",
                "Create movie posters with paper and colors",
                "Build a mini storybook with paper and crayons"
            ]
        }
    ];

    const faqs = [
        {
            question: "What ages do you accept for the summer camp?",
            answer: "Our summer camp welcomes children aged 4-12 years. Children are grouped as Little Adventurers (4-7 years) and Big Trailblazers (7+ years) with age-appropriate activities."
        },
        {
            question: "What should my child bring to camp each day?",
            answer: "Children should bring a water bottle, snacks and any necessary personal items. All items should be labeled with the child's name to avoid mismatches."
        },
        {
            question: "Do you provide food or snacks?",
            answer: "No, we don't provide food or snacks. Children should eat breakfast before arriving as the camp ends at 1:00 PM before lunch time. Please send a water bottle with your child."
        },
        {
            question: "Are there any discounts available?",
            answer: "Yes! We offer a 10% sibling discount for multiple children from the same family, and a 5% friends discount when registering together with friends."
        }
    ];

    return (
        <div className="summer-camp-page">
            <Navbar theme="summer" />

            <main>
                {/* Hero Section */}
                <section className="camp-hero">
                    <div className="floating-elements">
                        <div className="float-element sun">
                            <FontAwesomeIcon icon={faSun}/>
                        </div>
                        <div className="float-element tree">
                            <FontAwesomeIcon icon={faTree}/>
                        </div>
                        <div className="float-element leaf">
                            <FontAwesomeIcon icon={faLeaf}/>
                        </div>
                        <div className="float-element water">
                            <FontAwesomeIcon icon={faWater}/>
                        </div>
                        <div className="float-element camp">
                            <FontAwesomeIcon icon={faCampground}/>
                        </div>
                    </div>

                    <div className="container">
                        <div className="hero-content">
                            <div className="hero-badge bounce-animation">SUMMER 2025</div>

                            <h1 className="hero-title">
                                <span className="title-line">SUMMER</span>
                                <span className="title-line">ADVENTURE</span>
                                <span className="title-line">CAMP</span>
                            </h1>

                            <p className="hero-subtitle">Little Elly Kudlu | Ages 4-12 years</p>

                            <div className="hero-features">
                                <div className="hero-feature">
                                    <div className="feature-icon">
                                        <FontAwesomeIcon icon={faMagic}/>
                                    </div>
                                    <span>Magic & Creativity</span>
                                </div>
                                <div className="hero-feature">
                                    <div className="feature-icon">
                                        <FontAwesomeIcon icon={faFlask}/>
                                    </div>
                                    <span>Science Exploration</span>
                                </div>
                                <div className="hero-feature">
                                    <div className="feature-icon">
                                        <FontAwesomeIcon icon={faMusic}/>
                                    </div>
                                    <span>Music & Movement</span>
                                </div>
                            </div>

                            <div className="hero-details reveal">
                                <div className="detail-item">
                                    <div className="detail-icon-wrapper">
                                        <FontAwesomeIcon icon={faCalendarAlt}/>
                                    </div>
                                    <div className="detail-text">
                                        <strong>Camp Sessions</strong>
                                        <p>April 1-19 or April 21-May 8, 2025</p>
                                    </div>
                                </div>

                                <div className="detail-item">
                                    <div className="detail-icon-wrapper">
                                        <FontAwesomeIcon icon={faClock}/>
                                    </div>
                                    <div className="detail-text">
                                        <strong>Camp Hours</strong>
                                        <p>9:30 AM to 1:00 PM</p>
                                    </div>
                                </div>

                                <div className="detail-item">
                                    <div className="detail-icon-wrapper">
                                        <FontAwesomeIcon icon={faMapMarkerAlt}/>
                                    </div>
                                    <div className="detail-text">
                                        <strong>Location</strong>
                                        <p>Little Elly Campus, Kudlu, Bengaluru</p>
                                    </div>
                                </div>
                            </div>

                            <div className="hero-cta">
                                <div className="price-tag pulse-animation">
                                    <div className="price">₹4,999</div>
                                    <div className="price-text">per child</div>
                                </div>

                                <a href="#register" className="register-btn">
                                    <span>Register Now</span>
                                    <div className="btn-arrow">
                                        <FontAwesomeIcon icon={faArrowRight}/>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Tab Navigation */}
                <section className="camp-tabs">
                    <div className="container">
                        <div className="tab-navigation reveal">
                            <button
                                className={`tab-btn ${activeTab === 'activities' ? 'active' : ''}`}
                                onClick={() => handleTabChange('activities')}
                            >
                                <FontAwesomeIcon icon={faStar}/>
                                <span>Activities</span>
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'schedule' ? 'active' : ''}`}
                                onClick={() => handleTabChange('schedule')}
                            >
                                <FontAwesomeIcon icon={faClock}/>
                                <span>Daily Schedule</span>
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'age-groups' ? 'active' : ''}`}
                                onClick={() => handleTabChange('age-groups')}
                            >
                                <FontAwesomeIcon icon={faChild}/>
                                <span>Age Groups</span>
                            </button>
                        </div>
                    </div>
                </section>

                {/* Activities Content */}
                <section className="camp-activities" id="activities">
                    <div className="container">
                        <div className="section-header reveal">
                            <div className="section-icon">
                                <FontAwesomeIcon icon={faStar}/>
                            </div>
                            <h2 className="section-title">Fun-Filled Activities</h2>
                            <p className="section-subtitle">Our summer camp features a wide variety of exciting
                                activities for children of all ages</p>
                        </div>

                        <div className="activities-grid reveal">
                            {activities.map((activity, index) => (
                                <div className="activity-card" key={index}>
                                    <div className="activity-icon">
                                        <FontAwesomeIcon icon={activity.icon}/>
                                    </div>
                                    <h3>{activity.title}</h3>
                                    <p>{activity.description}</p>
                                    <ul className="activity-features">
                                        {activity.features.map((feature, idx) => (
                                            <li key={idx}>
                                                <FontAwesomeIcon icon={faCheck}/>
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>

                        <div className="finale-celebration reveal">
                            <div className="finale-icon">
                                <FontAwesomeIcon icon={faCertificate}/>
                            </div>
                            <h3>Special Finale Celebration!</h3>
                            <p>On the last day of each camp, both age groups come together for a special finale from 10
                                AM to 12 PM. Children display their crafts (pottery, masks, posters) in a mini
                                exhibition, play group games, and receive certificates. Everyone gets a small candy bag
                                as a treat. Parents are invited to join the fun!</p>
                        </div>
                    </div>
                </section>

                {/* Schedule Content */}
                <section className="camp-schedule" id="schedule">
                    <div className="container">
                        <div className="section-header reveal">
                            <div className="section-icon">
                                <FontAwesomeIcon icon={faClock}/>
                            </div>
                            <h2 className="section-title">Daily Schedule</h2>
                            <p className="section-subtitle">A Day at Summer Fiesta</p>
                            <div className="section-description">
                                <p>Our carefully structured daily schedule provides a balance of activities, ensuring
                                    children remain engaged and enjoy their summer camp experience.</p>
                            </div>
                        </div>

                        <div className="timeline reveal">
                            {timelineItems.map((item, index) => (
                                <div className="timeline-item" key={index}>
                                    <div className="timeline-dot"></div>
                                    <div className="timeline-content">
                                        <div className="timeline-time">{item.time}</div>
                                        <h3>{item.activity}</h3>
                                        <p>{item.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="camp-dates reveal">
                            <div className="date-option">
                                <h4>Camp 1</h4>
                                <p>April 1 - April 19, 2025</p>
                                <div className="date-features">
                                    <span>15 activity days + finale day</span>
                                </div>
                            </div>

                            <div className="date-divider">or</div>

                            <div className="date-option">
                                <h4>Camp 2</h4>
                                <p>April 21 - May 8, 2025</p>
                                <div className="date-features">
                                    <span>15 activity days + finale day</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Age Groups Content */}
                <section className="camp-age-groups" id="age-groups">
                    <div className="container">
                        <div className="section-header reveal">
                            <div className="section-icon">
                                <FontAwesomeIcon icon={faChild}/>
                            </div>
                            <h2 className="section-title">Age-Appropriate Fun</h2>
                            <p className="section-subtitle">Tailored activities for every age group</p>
                            <div className="section-description">
                                <p>We offer tailored activities for both Little Adventurers (ages 4-7) and Big
                                    Trailblazers (ages 7+) to ensure every child has a meaningful and enjoyable
                                    experience.</p>
                            </div>
                        </div>

                        <div className="age-groups-container reveal">
                            {ageGroups.map((group, index) => (
                                <div className="age-group-card" key={index}>
                                    <div className="age-group-header">
                                        <h3>{group.name}</h3>
                                        <div className="age-badge">{group.age}</div>
                                    </div>
                                    <ul className="age-group-activities">
                                        {group.activities.map((activity, idx) => (
                                            <li key={idx}>
                                                <FontAwesomeIcon icon={faCircle} className="dot-icon"/>
                                                {activity}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>

                        <div className="shared-activities reveal">
                            <h3>Shared Activities</h3>
                            <p>Both groups will also enjoy watching "The Incredibles" with popcorn and toppings during
                                movie day, participate in separate treasure hunts and scavenger hunts, and have various
                                dance sessions throughout the camp.</p>
                        </div>
                    </div>
                </section>

                {/* Registration Section */}
                <section className="camp-register" id="register">
                    <div className="container">
                        <div className="section-header reveal">
                            <div className="section-icon">
                                <FontAwesomeIcon icon={faCalendarAlt}/>
                            </div>
                            <h2 className="section-title">Register Now</h2>
                            <p className="section-subtitle">Limited spots available - secure your child's place
                                today!</p>
                        </div>

                        <div className="register-container reveal">
                            <div className="register-card">
                                <div className="register-price">
                                    <span className="currency">₹</span>
                                    <span className="amount">4,999</span>
                                    <span className="period">per child</span>
                                </div>

                                <div className="register-details">
                                    <h3>Summer Camp 2025</h3>
                                    <p>Choose from two identical sessions:</p>
                                    <ul>
                                        <li><FontAwesomeIcon icon={faCheck}/> April 1-19, 2025</li>
                                        <li><FontAwesomeIcon icon={faCheck}/> April 21-May 8, 2025</li>
                                    </ul>

                                    <div className="register-note">
                                        <p>Both sessions offer the same curriculum and activities</p>
                                    </div>

                                    <div className="registration-options">
                                        <h4 className="registration-title">Register Your Child Today</h4>

                                        <div className="registration-methods two-options">
                                            <div className="registration-method chatbot">
                                                <div className="method-icon">
                                                    <FontAwesomeIcon icon={faComments}/>
                                                </div>
                                                <div className="method-content">
                                                    <h6>Chat with Us</h6>
                                                    <p>Register instantly through our chatbot assistant below</p>
                                                </div>
                                            </div>

                                            <div className="registration-method phone">
                                                <div className="method-icon">
                                                    <FontAwesomeIcon icon={faPhone}/>
                                                </div>
                                                <div className="method-content">
                                                    <h6>Call Us</h6>
                                                    <p>Speak directly with our admissions team</p>
                                                    <a href="tel:+919019829264" className="method-link">+91 90198
                                                        29264</a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="discounts-box reveal">
                            <div className="discount-icon">
                                <FontAwesomeIcon icon={faUsers}/>
                            </div>
                            <div className="discount-content">
                                <h3>Special Discounts!</h3>
                                <p>10% sibling discount for multiple children | 5% friends discount when registering
                                    together</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="camp-faq" id="faq">
                    <div className="container">
                        <div className="section-header reveal">
                            <div className="section-icon">
                                <FontAwesomeIcon icon={faLightbulb}/>
                            </div>
                            <h2 className="section-title">Frequently Asked Questions</h2>
                            <p className="section-subtitle">Everything you need to know about our summer camp</p>
                        </div>

                        <div className="faq-grid reveal">
                            {faqs.map((faq, index) => (
                                <div className="faq-item" key={index}>
                                    <h3>{faq.question}</h3>
                                    <p>{faq.answer}</p>
                                </div>
                            ))}
                        </div>

                        <div className="contact-info reveal">
                            <p>Have more questions? Call our team <a href="tel:+919019829264" className="contact-link">+91
                                90198 29264</a></p>
                        </div>
                    </div>
                </section>
            </main>

            <Footer theme="summer"/>
            <ScrollToTop theme="summer"/>
        </div>
    );
};

export default SummerCampPage;